import { inject, Injectable } from '@angular/core';
import { forEach, isEmpty, get, includes, isArray, find, map, isFunction } from 'lodash-es';
import { isDefined } from '@wm/core';
import { BindTreeUtilsService, I18nService, ProjectService, PrefabService } from '@wms/services';
import { addNodeToJson, checkSpecialCharacters, isImageFile, isString, isUndefined, REGEX } from '@wms/core';
import { Observable, Subject } from 'rxjs';
import { PageWsService } from '@wms/workspace';
import { getServiceTypesByCollectionType, getStudioCopy } from '@wms/variables';
import { getWidgetRegistry } from '@wm/components-base';

@Injectable({
  providedIn: 'root'
})
export class BindVariableService {

  dialogParams: any;
  variableMode: any;
  shouldLoadTabs: boolean;
  categories: any;
  allowedCategories: any = {};
  variablesTree: any = [];
  variablesPanelTree: any = [];
  // variables panel tree contains list of variables & actions
  widgetsTree: any = [];
  mixedTree: any = [];
  bindMapTree: any;
  filteredVariablesTree: any = [];
  setActiveTabName: any;
  showWarning: boolean = false;
  filteredWidgetsTree: any;
  filteredMixedTree: any = [];
  allWidgetsNotFound: any;
  allVariablesNotFound: any = false;
  activeTab: any;
  currentTab: any = '';
  selectedNodeProps: any;
  activeBindNode: any;
  validation: any = {};
  activeProperty: any = {};
  warningMessage: any = {};
  selectedNode: any;
  disableBind: boolean;
  bindActive: any;
  filetype: any;
  selectedResourceType: any;
  prevBoundEntity: any;
  appMessagesInitialized: any;
  variableCategoryList: any;
  bindVariableMessage: any;
  useExprEditorContent: any;
  pageParamsTree: any;
  pageHasInputs: any;
  prefabPropsTree: any = [];
  isWidgetsLoaded: boolean = false;
  isVariablesLoaded: boolean = false;
  displayExprTeeLoaded: boolean = false;
  appMessagesSubject = new Subject();
  updateEditorContentSubject = new Subject();
  locale: any;
  i18Service: I18nService;
  pageWsService: PageWsService;
  bindTreeService: BindTreeUtilsService;
  projectService: ProjectService;
  prefabService: PrefabService;
  isTemplateBundleType: boolean;
  isPrefabTemplate: boolean;
  isPrefabType: boolean;
  selectedNodeInBindDialog: any = {variable:{}, widget:{}, resource:{}, expression:{}};
  appLocaleBindExpr: string;
  CATEGORIES = {
    'VARIABLES': 'VARIABLES',
    'MESSAGES': 'MESSAGES',
    'EXPRESSION': 'EXPRESSION',
    'WIDGETS': 'WIDGETS',
    'RESOURCE': 'RESOURCE'
  };
  tabs: any = ['variable', 'widget', 'resource', 'expression', 'message'];

  constructor() {
    this.bindTreeService = inject(BindTreeUtilsService);
    this.i18Service = inject(I18nService);
    this.pageWsService = inject(PageWsService);
    this.projectService = inject(ProjectService);
    this.prefabService = inject(PrefabService);
    this.locale = this.i18Service.getMessages();
    this.isTemplateBundleType = this.projectService.isTemplateBundleType();
    this.isPrefabTemplate = this.projectService.isPrefabType();

    forEach(this.CATEGORIES, (k: any) => {
      this.allowedCategories[k] = isDefined(k) ? k : true;
    });
  }

  handleTreeNodeClick($event: any) {
    this.validateBinding($event.dataItem.id, $event.dataItem.props);
  }

  /**
   * Sets the selectedNodeProp object to respective tabs value
   * @param nodeProps, the value selected in the tab
   */
  setSelectedNodePropObjVal = (nodeProps: any) => {
    if (nodeProps.bindable) {
      this.selectedNodeProps[this.tabs[this.activeTab.index]] = nodeProps;
    } else {
      this.selectedNodeProps[this.tabs[this.activeTab.index]] = {};
    }
  }

  /*
   * toggles binding validation status in the view
   * @param status current status of the binding(true/false)
   */
  toggleValidation = (status: any, currentTab: any) => {
      let selectedNodeProperties = this.selectedNodeProps[currentTab];
      this.validation[currentTab] = this.validation[currentTab] || {};
      this.validation[currentTab].show = true;
      if (status) {
        this.validation[currentTab].message = this.locale.LABEL_VARIABLE_BOUND_ERROR_MESSAGE.replace('${0}', [currentTab]);
        this.validation[currentTab].icon = 'done';
      } else {
        this.validation[currentTab].message = this.locale.LABEL_VARIABLE_BOUND_SUCCESS_MESSAGE;
        this.validation[currentTab].icon = 'error';
      }
      this.validation[currentTab].selectedVariableType = this.bindTreeService.getNodeInfoLabel(get(selectedNodeProperties, 'format') || get(selectedNodeProperties, 'datatype'), selectedNodeProperties?.bindingType, true);
      this.validation[currentTab].boundExpression = this.getSelectedNodeVal(selectedNodeProperties);
  }

  /**
   * gets the value from the selected node.
   * the value extraction is different for different tabs
   * @param nodeProps
   * @returns {*}
   */
  getSelectedNodeVal = (nodeProps: any) => {
    let type = nodeProps && nodeProps.type;
    switch (type) {
      case 'variable':
      case 'widget':
      case "prefab-props":
      case "page-param":
        return nodeProps.value;
      case 'resource':
        return nodeProps.path;
      case 'message':
        return 'appLocale["' + nodeProps.value + '"]';
      default:
        return 'Invalid Node';
    }
  }

  //maintaining and updating selectedNode value of each tab separately
  setSelectedNodeInBindDialog =(nodeId?: any, nodeProps?: any) => {
    this.selectedNodeInBindDialog[this.currentTab] = { dataItem: { id: nodeId, props: nodeProps } };
    if((this.currentTab === 'variable' || this.currentTab === 'widget') && (this.dialogParams?.boundEntity?.toString().includes(nodeProps?.value))){
      this.selectedNodeInBindDialog['expression'] = { dataItem: { id: nodeId, props: nodeProps } };
    }
  }

  /**
   * validates the binding of selected tree node and activeProperty
   * @param nodeId
   * @param nodeProps
   */
  validateBinding = (nodeId?: any, nodeProps?: any) => {
    //Consider nodeProps passed or get previous selected nodeprops from scope
    nodeProps = nodeProps || this.getSelectedNodePropObj();

    if (isEmpty(nodeProps)) {
      return;
    }
    if (!nodeProps.bindable) {
      this.setSelectedNodePropObjVal(nodeProps);
      this.setSelectedNodeInBindDialog(nodeId, nodeProps);
      this.toggleValidation(false, this.currentTab);
      this.disableBind = true;
      return;
    }

    this.disableBind = false;
    this.setSelectedNodePropObjVal(nodeProps);
    this.setSelectedNodeInBindDialog(nodeId, nodeProps);

    let srcTypes = this.activeProperty.type,
      srcBindingType = this.activeProperty.bindingType,
      tgtTypes = nodeProps.datatype,
      tgtBindingType = nodeProps.bindingType,
      selectedNodeProperties = this.getSelectedNodePropObj();;

    srcTypes = this.bindTreeService.cleanseTypes(srcTypes, this.activeProperty.format);
    tgtTypes = this.bindTreeService.cleanseTypes(tgtTypes, nodeProps.format);

    switch (nodeProps.type) {
      case 'variable':
      case 'widget':
      case "prefab-props":
      case "page-param":
        this.bindActive = this.bindTreeService.validateVariableWidgetBinding({
          'types': srcTypes,
          'bindingType': srcBindingType
        }, {
          'types': tgtTypes,
          'bindingType': tgtBindingType
        });
        break;
      case 'resource':
        this.filetype = "folder";
        if (selectedNodeProperties.datatype !== "folder") {
          this.filetype = "image";
          if (!isImageFile(selectedNodeProperties.path)) {
            this.filetype = "";
            let index = selectedNodeProperties.path.lastIndexOf(".");
            if (index !== -1) {
              this.filetype = selectedNodeProperties.path.substring(index + 1);
            }
          }
        }

        /* bind only in case the required property is of type string */
        this.bindActive = includes(srcTypes, 'string');
        // if datatype is folder then dont throw validation error in binding dialog for image resource
        if (this.selectedResourceType === 'Image') {
          this.bindActive = (selectedNodeProperties.datatype !== 'folder') && this.bindActive;
        }
        break;
      case "message":
        /* bind only in case the required property is of type string */
        this.bindActive = includes(srcTypes, 'string');
        break;
    }

    this.toggleValidation(this.bindActive, this.currentTab);

    //Skip content set is click is on bindTree's node as the content is already updated
    if (!this.activeProperty.variableBindTree) {
      this.updateEditorContentSubject.next({ 'set': '' });
      this.onMixedTreeClick(nodeId, nodeProps);
    } else {
      this.activeProperty.variableBindTree = false;
    }
  }

  /**
   * This function handles the mixed tree node click
   * and is called in two cases
   * 1. when a widget is clicked
   * 1. when a variable is clicked
   * @param nodeId
   * @param nodeUId
   * @param nodeProps
   */
  onMixedTreeClick = (nodeId: any, nodeProps: any) => {
    if(this.dialogParams.activeProperty.propertyDetails.bindonly === 'expression'){
      this.handleDisplayExprMixedTree(nodeProps);
      this.setSelectedNodeInBindDialog(nodeId, nodeProps);
    }
    else{
      this.handleMixedTree(nodeId, nodeProps);
    }
  }

  //to be called only when binding fields other than display expression
  handleMixedTree = (nodeId: any, nodeProps: any) => {
    if (!nodeProps || !nodeProps.bindable) {
      this.disableBind = true;
      this.setSelectedNodeInBindDialog(nodeId, nodeProps);
      return;
    }
    // For partials inside a Prefab, the Prefab properties are accessible as Prefab.propertyname
    if (get(nodeProps, 'type') === 'prefab-props' && get(this.pageWsService.activePageInfo, 'type') === 'PARTIAL') {
      if (!nodeProps.value.startsWith('Prefab.')) {
        nodeProps.value = 'Prefab.' + nodeProps.value;
        if (this.currentTab && this.validation[this.currentTab]) {
          this.validation[this.currentTab].boundExpression = nodeProps.value;
        }
      }
    }
    this.disableBind = false;
    this.setSelectedNodeInBindDialog(nodeId, nodeProps);
    switch (nodeProps.type) {
      case "widget":
        this.insertText("Widgets." + nodeProps.value);
        break;
      case "variable":
        this.insertText("Variables." + nodeProps.value);
        break;
      case "message":
        const templateVariables = [...nodeId.value.matchAll(REGEX.REPLACE_PATTERN)];
        if (templateVariables.length) {
          const templateReplaceObj: { [key: string]: string } = {};
          templateVariables.forEach(match => {
            templateReplaceObj[match[1]] = '';
          });
          this.insertText(`appLocale.${nodeProps.value} | templateReplace: ${JSON.stringify(templateReplaceObj)}`);
          this.activeTab = { expression: true, index: 3 };
          this.currentTab = this.tabs[3];
        } else {
          this.insertText("appLocale." + nodeProps.value);
        }
        break;
      case "prefab-props":
      case "page-param":
        this.insertText(nodeProps.value);
        break;
      default:
        break;
    }
  }

  //to be called only when binding for display expression
  handleDisplayExprMixedTree = (nodeProps: any) => {
      if (!nodeProps || !nodeProps.bindable) {
        this.disableBind = true;
        return;
      }
      let shortEpr = nodeProps.value;
      this.disableBind = false;
      //Handling "." for regular use case and "[" for column name with spaces
      //Ex: dataSet[$i].ID or dataSet[$i]['BigDec Col']
      let expr = new RegExp(this.getVariableName() + '.dataSet([\\w\\.\\[\\]\\$\'\\s])*'),
        matchExpr;
      /*check if the node is a variable node & it has a nodeValue */
      if (shortEpr) {
        matchExpr = this.getBindExpr().match(expr);
        matchExpr = matchExpr ? matchExpr[0] : '';
        shortEpr = shortEpr.replace(matchExpr + '[$i].', "");
        shortEpr = shortEpr.replace(matchExpr + '.', "");
        shortEpr = shortEpr.replace(matchExpr, "");
        this.insertText("$[" + shortEpr + "]");
      }
    };

  /**
   * Inserts text at the cursor position or at the end
   * @param text text to be inserted
   */
  insertText = (text: any) => {
    this.useExprEditorContent = text;
    this.updateEditorContentSubject.next({ 'update': text });
  }

  /**
   * get selectedNodeProp value
   */
  getSelectedNodePropObj() {
    return this.selectedNodeProps[this.tabs[this.activeTab.index]];
  }

  /**
   * get children of node
   * @param node parent node
   */
  getChildren = (node: any) => {
    let options: any;
    return this.bindTreeService.getChildrenForNode(node, options);

  }

  /**
   * check if the tree is empty
   * @param value
   */
  isTreeEmpty(tree: any) {
    return tree[0]?.prefabname? false : (isEmpty(get(tree, [0, 'children'])) && isEmpty(get(tree, [1, 'children'])) && !isFunction(get(tree, [0, 'children'])) && !isFunction(get(tree, [1, 'children']))) ;
  }

  /*
   * returns true if the name is in path of bound entity
   * @param name, id/ name of the current node
   */
  isInBoundPath = (name: any) => {
    return (this.bindTreeService.isBoundValue && this.bindTreeService.boundEntityValue.indexOf(name) > -1);
  }

  /*
   * returns true if the name is the bound entity. Used for resource trees.
   * @param name, id/ name of the current node
   */
  isActiveEntityResource = (name: any) => {
    return this.bindTreeService.isPrevBoundValue ? false : (this.bindTreeService.isBoundValue && this.bindTreeService.boundEntityValue.split("/").pop() === name);
  }

  initVariables(fetchActions: boolean = false, setActiveTab = true) {
    this.variablesTree = [];
    this.filteredVariablesTree = [];
    this.filteredWidgetsTree = [];
    this.filteredMixedTree = [];
    //this.mixedTree = [];
    this.selectedNodeInBindDialog = {variable:{}, widget:{}, resource:{}, expression:{}};
    if(this.isPrefabTemplate) {
      this.selectedNodeInBindDialog['prefab-props'] = {};
    }
    this.warningMessage = {};
    this.validation = {};
    this.appMessagesInitialized = false;
    this.selectedNodeProps = {};
    this.setActiveTabName = this.dialogParams?.setActiveTabName;
    this.bindTreeService.isCurrBoundValue = this.getIsCurrBoundValue();
    this.bindTreeService.isPrevBoundValue = this.getIsPrevBoundValue();
    this.bindTreeService.isBoundValue = this.bindTreeService.isCurrBoundValue || this.bindTreeService.isPrevBoundValue;
    this.bindTreeService.boundEntityValue = this.bindTreeService.isPrevBoundValue ? this.dialogParams?.prevBoundEntity : this.dialogParams?.boundEntity;

    /* If bindMode is variable and nestedBindMode is true then set variableMode to true*/
    this.setVariableMode();

    /*variableCategoryList contains all variables of all categories */
    this.createVariableCategoryList();

    /* update current and active Tab */
    if(setActiveTab) {
      this.updateActiveTab();
    }

    /* update active property details and
    call particular actions to be done based on bindonly attr*/
    this.updateActivePropertyDetails(fetchActions);
  }

  /*
   * sets the active tab to the specified tab
   * @param tab the tab to be set as activeTab
   */
  setActiveTab = (tab: any, tabIndex: any) => {
    if (this.activeTab && this.activeTab[tab]) {
      return;
    } else if (isUndefined(tabIndex)) {
      tabIndex = 0;
    }
    this.activeTab = { index: tabIndex };
    this.activeTab[tab] = true;
    this.currentTab = tab;
    this.showWarning = false;
  }

  /* update values of activeTab and currentTab based on boundPath */
  updateActiveTab() {
    if (this.setActiveTabName) {
      this.setActiveTab(this.setActiveTabName, this.tabs.indexOf(this.setActiveTabName));
    } else {
      this.appLocaleBindExpr = this.isPrefabTemplate && this.projectService.getPageInfo(this.pageWsService.activePageName).type === 'PARTIAL' ? 'bind:Prefab.appLocale' : 'bind:appLocale';
      if (this.bindTreeService.isInBoundPath('bind:Variables')) {
        this.activeTab = { variable: true, index: 0 };
        this.currentTab = this.tabs[0];
      } else if (this.bindTreeService.isInBoundPath('bind:Widgets') || this.isTemplateBundleType) {
        this.activeTab = { widget: true, index: 1 };
        this.currentTab = this.tabs[1];
      } else if (this.bindTreeService.isInBoundPath('resources/')) {
        this.activeTab = { resource: true, index: 2 };
        this.currentTab = this.tabs[2];
      } else if (this.bindTreeService.isInBoundPath(this.appLocaleBindExpr)) {
        this.activeTab = { message: true, index: 4 };
        this.currentTab = this.tabs[4];
      } else if (this.isPrefabTemplate && this.bindTreeService.isInBoundPath('bind:')) {
        if (this.bindTreeService.isInBoundPath('bind:pageParams')) {
          this.activeTab = { 'page-param': true, index: 6 };
          this.currentTab = 'page-param';
        } else {
          this.activeTab = { 'prefab-props': true, index: 5 };
          this.currentTab = 'prefab-props';
        }
      } else if (this.bindTreeService.isInBoundPath('bind:pageParams')) {
        this.tabs.push('page-param');
        this.activeTab = { 'page-param': true, index: 5 };
        this.currentTab = this.tabs[5];
      } else if (isString(this.dialogParams?.boundEntity) && this.dialogParams?.boundEntity.indexOf('bind:') > -1) {
        this.activeTab = { 'expression': true, index: 3 };
        this.currentTab = this.tabs[3];
      } else {
        this.activeTab = { variable: true, index: 0 };
        this.currentTab = this.tabs[0];
      }
    }
  }

  getIsCurrBoundValue() {
    return this.validateBoundEntity(this.dialogParams?.boundEntity);
  }

  getIsPrevBoundValue() {
    return !this.bindTreeService.isCurrBoundValue && this.validateBoundEntity(this.dialogParams?.prevBoundEntity);
  }

  validateBoundEntity(entity: any) {
    return (entity && isString(entity) && (entity.indexOf('bind:') > -1 &&
      !this.bindTreeService.expressionRegExp.test(entity)) ||
      entity?.toString().indexOf('resources/') > -1)
  }

  initDataBindTree() {
    this.bindMapTree = [];
    let bindMap = this.dialogParams?.activeProperty.bindMap;
    if (this.dialogParams) {
      this.dialogParams.activeProperty.existingBindings = this.dialogParams?.activeProperty.existingBindings || [];
    }
    if (bindMap && bindMap.length) {
      this.processBindNodes(bindMap);
    }
  }

  /**
   * finds the binding object against passed target x-path key among existingBindings of active variable
   * @param bindTarget x-path of against which binding object to be found
   * @param createIfNotExists if true, will create a new object of not found
   * @returns found binding object
   */
  getBindingObjectFromList(bindTarget: any, createIfNotExists?: any) {
    let existingBindings = this.dialogParams?.activeProperty.existingBindings || [];
    if (!isArray(existingBindings)) {
      existingBindings = this.dialogParams?.activeProperty.existingBindings[this.dialogParams?.activeProperty.propertyDetails.inputType];
    }
    let targetBindNode = find(existingBindings, (obj: any) => {
      return obj.target === bindTarget;
    });
    targetBindNode = targetBindNode || (createIfNotExists && existingBindings[existingBindings.push({ "target": bindTarget }) - 1]);

    return targetBindNode;
  }

  processBindNodes(bindNodes: any, parentNode?: any) {
    let curNodeLabel,
        badgeLabel,
      curNode,
      propertyBindType,
      bindTarget,
      bindTargetObj,
      isActive;
    bindNodes.forEach((bindNode: any) => {
      /* CHECK IF THE LABEL CAN BE BOUND DIRECTLY TO THE BIND.VALUE */
      bindTarget = (parentNode && !parentNode.props.isRoot ? parentNode.props.bindTarget + '.' : '') + bindNode.name;
      bindTargetObj = this.getBindingObjectFromList(bindTarget) || {};
      curNodeLabel = bindNode.name;
      badgeLabel = ((bindTargetObj.value)?.toString().replace("bind:", "").replace("Variables.", "").replace("Widgets.", "") || '');
      isActive = bindTarget === this.dialogParams?.activeProperty.propertyDetails.modelObj.target;
      propertyBindType = this.bindTreeService.getBindingType(bindNode.type, bindNode.isList);
      curNode = addNodeToJson(this.bindMapTree, bindNode.name, parentNode && parentNode.uid, {
        "active": isActive,
        "label": curNodeLabel,
        "badgeLabel": badgeLabel,
        "nodeProps": {
          "type": bindNode.type,
          "format": bindNode.format,
          "isList": bindNode.isList,
          "model": bindNode,
          "bindingType": propertyBindType,
          "bindTarget": bindTarget,
          "isRoot": !parentNode ? true : false
        }
      });

      if (bindNode.fields && bindNode.fields.length) {
        this.processBindNodes(bindNode.fields, curNode);
      }
      if (isActive) {
        this.activeBindNode = {
          'props': curNode.props,
          'id': bindNode.name,
          'self': curNode
        };
        this.resetEditorContent();
      }
    });
  }

  /**
  * sets the current selected property value as the content of editor.
  */
  resetEditorContent() {
    let targetBindNode = this.getBindingObjectFromList(this.activeBindNode.props.bindTarget);
    if (!targetBindNode || isUndefined(targetBindNode.value)) {
      this.updateEditorContentSubject.next({ 'set': '' });
    } else if (isString(targetBindNode.value) && /^(\s*bind:)/.test(targetBindNode.value)) {
      this.updateEditorContentSubject.next({ 'set': targetBindNode.value.split('bind:')[1] });
    } else {
      if (targetBindNode.value) {
        this.updateEditorContentSubject.next({ 'set': targetBindNode.value.toString() });
      }
    }
  }

  /* If it is in variableMode then initialise DataBindTree*/
  setVariableMode() {
    if (this.dialogParams?.bindMode === 'variable' && this.dialogParams?.nestedBindMode) {
      this.variableMode = true;
      this.initDataBindTree();
    }
  }

  /* create a list containing option for showing all variables of all categories */
  createVariableCategoryList() {
    this.variableCategoryList = getServiceTypesByCollectionType('data');
    this.variableCategoryList.push('all');
    this.variableCategoryList = map(this.variableCategoryList, (value) => {
      return {
        'key': value,
        'value': value,
        'label': this.locale.LABEL_VARIABLE_SERVICETYPE[value]
      };
    });
  }

  /* check, if there is attr called 'bindonly' which only needs expression to be shown */
  updateActivePropertyDetails(fetchActions: boolean) {
    if (this.dialogParams?.activeProperty &&
      this.dialogParams?.activeProperty.propertyDetails) {
      //Initializes bound entity, prev bound entity and other config needed for bind tree service.
      this.bindTreeService.setBindMapConfig(this.dialogParams, this.validateBinding);
      /*assigning the widget name and it's property that is being bound, to the scope.*/

      let defaultSelectedNode: any;
      if (this.bindMapTree) {
        defaultSelectedNode = this.bindMapTree[0].children?.find((node: any) => node.id === this.dialogParams?.activeProperty.name);
        this.selectedNode = { dataItem: defaultSelectedNode, id: defaultSelectedNode?.id };
      }
      this.activeProperty.name = this.dialogParams?.activeProperty.propertyOwnerName + '.' + this.dialogParams?.activeProperty.name;
      this.activeProperty.type = this.dialogParams?.activeProperty.propertyDetails.type;
      this.activeProperty.format = this.dialogParams?.activeProperty.propertyDetails.format;
      this.activeProperty.isList = this.dialogParams?.activeProperty.propertyDetails.isList;
      if (this.dialogParams?.bindMode !== 'variable') {
        let widgets: any = getWidgetRegistry(),
          activeWidget = widgets.get("wm_" + this.dialogParams?.activeProperty.propertyOwnerName),
          getIsListFrom = this.dialogParams?.activeProperty.propertyDetails.getIsListFrom,
          getTypeFrom = this.dialogParams?.activeProperty.propertyDetails.getTypeFrom,
          propertyType;
        if (!activeWidget) {
          activeWidget = this.getActiveWidget();
        }
        if (activeWidget && getIsListFrom) {
          this.activeProperty.isList = this.bindTreeService.evalIsListFromExp(getIsListFrom, activeWidget);
        }
        if (getTypeFrom) {
          propertyType = this.bindTreeService.evalTypeFromExp(getTypeFrom, activeWidget);
        }
        this.activeProperty.type = propertyType || this.activeProperty.type;
      }
      this.activeProperty.bindingType = this.bindTreeService.getBindingType(this.activeProperty.type, this.activeProperty.isList);
      this.activeProperty.typeLabel = this.bindTreeService.getNodeInfoLabel(this.activeProperty.format || this.activeProperty.type, this.activeProperty.bindingType, true);
      if (this.dialogParams?.message) {
        this.bindVariableMessage = this.dialogParams?.message;
      }
      /*based on the bindonly attribute, call the particular actions to be done*/
      switch (this.dialogParams?.activeProperty.propertyDetails.bindonly) {
        case "expression":
          this.setActiveTab('expression', 3);
          this.buildDisplayExprTree();
          this.displayExprTeeLoaded = true;
          this.shouldLoadTabs = true;
          break;
        default:
          /*If a entity is bound, Using timeout to delay till tree is created on UI
           so that scroll to bound entity works on tree*/
          if (this.bindTreeService.isBoundValue) {
            setTimeout(this.defaultAction, undefined, false);
          } else {
            this.defaultAction(fetchActions);
          }
      }
    } else {
      /*If a entity is bound, Using timeout to delay till tree is created on UI
       so that scroll to bound entity works on tree*/
      if (this.bindTreeService.isBoundValue) {
        setTimeout(this.defaultAction, undefined, false);
      } else {
        this.defaultAction(fetchActions);
      }
    }
  }

  getActiveWidget(){
    let widgets: any = getWidgetRegistry();
    return widgets.get(this.dialogParams.activeProperty.widgetId);
  }

  getBindExpr(){
    let activeWidget = this.getActiveWidget();
    return activeWidget.binddataset || activeWidget.bindactions;
  }

  getVariableName(){
    return this.getBindExpr().replace("bind:Variables.", "").split(".")[0]
  }
  /*
   * to show the variable properties in the variable tree in display expression dialog
   */
  buildDisplayExprTree = () => {
    let variableCollection = getStudioCopy(),
      widgetName = this.dialogParams.activeProperty.propertyOwnerName,
      bindEpr: any = null,
      variableName: any,
      variableObj: any,
      activeWidget = this.getActiveWidget();

    /*sanity check; if widget exists*/
    if (widgetName && activeWidget) {
      /*Added bindactions to support actions in panel widget*/
      bindEpr = this.getBindExpr();
      variableName = bindEpr;
      variableObj = {};
      /*sanity check; if variable exists*/
      if (variableName) {
        /*get the variable name from the bind dataSet*/
        variableName = this.getVariableName();
        /*retrieve variable object*/
        variableObj[variableName] = variableCollection['App'][variableName] || variableCollection[this.dialogParams.activePageName][variableName];
        /*get the variable name from the bind dataSet & retrieve variable object*/
        this.bindTreeService.createVariablesTreeObject(variableObj, true, this.variablesTree);
        this.mixedTree = [];
        this.buildMixedTree(true);

        // if prefab project, push prefab props into the Mixed tree. Prefab properties will be loaded in expression tab.
        if (this.isPrefabTemplate) {
          this.tabs.push('prefab-props');
          this.tabs.push('page-param');
          this.getPrefabExposedProps();
        }

      } else {
        /* display expression can be set with dataset as null for the rating widget in readonly mode */
        if (activeWidget.widgettype === 'wm-rating' && activeWidget.readonly) {
          this.defaultAction();
        } else {
          /*warn user if binddataset is empty*/
          this.showWarning = true;
          this.warningMessage = {
            type: "warning",
            caption: this.locale["MESSAGE_DATASET_NOT_SELECTED_FOR_DISPLAYEXPR"]
          }
        }
      }
    }
  }

 /*
  * gets the variables and creates variable-tree
  * @param variablesCollection collection received from the Variables service
  */
  getVariables = (variablesCollection: any, fetchActions: boolean) => {
    if (this.isVariablesLoaded) {
      return;
    }
    this.variablesTree = this.bindTreeService.getRootStructure(variablesCollection);
    this.bindTreeService.buildVariables(variablesCollection, this.variablesTree, fetchActions, null);
    this.buildMixedTree();
    this.isVariablesLoaded = true;
    this.allVariablesNotFound = this.isTreeEmpty(this.filteredVariablesTree);
  }


  getPrefabExposedProps = () => {
    (this.prefabService.getConfig() as Promise<any>).then((config: any) => {
      let properties = (config && config.properties) || {},
        propsArray: any = [],
        propsTreeObj,
        i = 0,
        isActive,
        boundProperty = this.bindTreeService.isBoundValue ? this.bindTreeService.boundEntityValue.split(":").pop() : undefined,
        isTreeExpanded = false;
      if (get(this.pageWsService.activePageInfo, 'type') === 'PARTIAL' && boundProperty) {
        boundProperty = boundProperty.replace('Prefab.', '');
      }
      forEach(properties, (prop: any, name: any) => {
        /* not displaying the current property */
        if (this.dialogParams?.bindMode === 'prefab-props' && name === this.dialogParams?.activeProperty.name) {
          return;
        }
        if (prop.type === 'event' || prop.type === 'method') {
          return;
        }
        isActive = this.bindTreeService.isPrevBoundValue ? false : (name === boundProperty);
        propsArray[i] = {
          'id': name,
          'class': "info",
          'bindable': true,
          'props': {
            'value': name,
            'type': 'prefab-props',
            'bindable': true,
            'datatype': prop.type,
            'bindingType': prop.type
          },
          'active': !isActive
        };

        if (isActive) {
          isTreeExpanded = true;
          this.validateBinding(propsArray[i].id, propsArray[i].props);
        }
        i = i + 1;
      });

      propsTreeObj = {
        id: this.locale.LABEL_PREFAB_PROPERTIES,
        children: propsArray,
        props: {},
        collapsed: !isTreeExpanded
      };

      //TO-DO mixed tree order in use expression tab
      this.prefabPropsTree = [propsTreeObj];
      const exists = this.mixedTree.some((obj: { id: string }) => obj.id === propsTreeObj.id);
      if (!exists) {
          this.mixedTree = [...this.mixedTree, propsTreeObj];
        }
        this.filteredMixedTree = this.mixedTree;
        this.filteredMixedTree = this.filteredMixedTree.filter((node: any) => {
            return !(node.id === 'Prefab Properties' && (!node.children || node.children.length === 0));
        });
    });
  }

  /*
* to build the variables & widgets mixed tree
*/
  buildMixedTree = (isTreeExpanded?: any) => {
    this.filteredVariablesTree = this.variablesTree;
    isTreeExpanded = this.bindTreeService.isInBoundPath('bind:Variables') ? true : isTreeExpanded;
    //this.mixedTree = [];
    if (this.allowedCategories) {
      this.mixedTree.push({
        id: "Variables",
        children: this.variablesTree,
        props: {},
        collapsed: !isTreeExpanded
      });
    }
    this.filteredMixedTree = this.mixedTree;
  }

  private fetchVariables(fetchActions: boolean) {
    let pages;
    //this.bindMode = dialogParams.bindMode;
    //If source variable is an app variable then showing only app variables
    if (get(this.dialogParams, 'activeProperty.variableOwner') === 'App') {
      pages = ['App'];
    }
    this.getVariables(getStudioCopy(pages), fetchActions);
  }

  /*
   * default action, if no option is provided specifically for the dialog
   * get the variables list and prepare the collection in required format for UI
   * get the widgets list
   */
  defaultAction = (fetchActions: boolean = false) => {
    /* get the variables list and prepare the collection in required format for UI*/
    if (this.activeTab.index === 0 || this.activeTab.index === 3) {
      this.fetchVariables(fetchActions);
    }
    if (!this.isWidgetsLoaded) {
      this.getWidgets();
    }
    if (this.activeTab[this.tabs[4]]) {
      this.appMessagesSubject.next(true);
    }

    /* if prefab project, push prefab props into the Mixed tree */
    if (this.isPrefabTemplate) {
      this.tabs.push('prefab-props');
      this.getPrefabExposedProps();
      this.getPageParamsTree();
    } else {
      this.getPageParamsTree();
    }
    this.shouldLoadTabs = true;
  }

  /**
 * Creates the tree data structure for PageParams tree
 */
  getPageParamsTree = () => {
    let pageInfo = this.projectService.getPageInfo(this.pageWsService.activePageName),
      pageParams = get(pageInfo, 'params') || [],
      propsArray: any = [],
      isActive,
      name,
      boundProperty = this.bindTreeService.isBoundValue ? this.bindTreeService.boundEntityValue.split(":").pop().replace('pageParams.', '') : undefined;

    this.pageHasInputs = pageParams.length;
    this.tabs.push('page-param');
    pageParams.forEach((param: any, i: any) => {
      name = param.name;
      isActive = (name === boundProperty);
      propsArray[i] = {
        'id': checkSpecialCharacters(name),
        'class': "info",
        'bindable': true,
        'props': {
          'value': 'pageParams.' + name,
          'type': 'page-param',
          'bindable': true,
          'datatype': param.type,
          'bindingType': param.type
        },
        'active': isActive
      };
      if (isActive) {
        this.validateBinding(propsArray[i].id, propsArray[i].props);
      }
    });

    if (propsArray.length) {
      let pageParamsObject = {
        id: 'pageParams',
        children: propsArray,
        props: {},
        collapsed: false
      };
      this.pageParamsTree = [pageParamsObject];
      this.mixedTree.push(pageParamsObject);
    }
  }

  /*get the widgets list*/
  getWidgets() {
    let widgetTreeObject: any = [],
      isTreeExpanded: any;
    this.bindTreeService.buildWidgets(widgetTreeObject, this.variablesTree);
    this.widgetsTree = widgetTreeObject;
    this.filteredWidgetsTree = this.widgetsTree;
    isTreeExpanded = this.bindTreeService.isInBoundPath('bind:Widgets');
    // If widgets tab is hidden, then do not push widgetsTree into MixedTree.
    if (this.allowedCategories.WIDGETS) {
      this.mixedTree.push({
        id: "Widgets",
        children: this.widgetsTree,
        props: {},
        collapsed: !isTreeExpanded
      });
    }
    this.filteredMixedTree = this.mixedTree;
    this.allWidgetsNotFound = this.isTreeEmpty(this.filteredWidgetsTree);
    this.isWidgetsLoaded = true;
  }
}

